import SeatContext from './SeatContext'
import React, { useState, useCallback } from 'react'

const SeatState = (props) => {
  const host = process.env.REACT_APP_BACKEND_URL;
    const seatsdata = []
      const [seats, setSeats] = useState(seatsdata)

// Get all seats
const getAllSeats = useCallback(async () => {
  console.log('getAllSeats called'); // Debug log
  try {
    const response = await fetch(`${host}/seats/fetchallseats/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "auth-token": localStorage.getItem('token'), // Replace with your actual auth token
      },
    });
    const json = await response.json();
    if (!Array.isArray(json)) {
      console.error("Unexpected seats response:", json);
      return;
    }
    // Sort by seatNumber as string (supports alphanumeric like W1, A10)
    const sortedSeats = [...json].sort((a, b) =>
      String(a.seatNumber ?? '').localeCompare(String(b.seatNumber ?? ''), undefined, { numeric: true, sensitivity: 'base' })
    );
    setSeats(sortedSeats);
  } catch (error) {
    console.error("Error fetching all students:", error);
    // Handle the error, you might want to set an error state or display a message to the user
  }
}, [host])


// Delete a seat

const deleteSeat = async (id) => {
  try {
    const response = await fetch(`${host}/seats/delete-seat/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': localStorage.getItem('token'),
      },
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || 'Failed to delete seat');
    }
    setSeats(prevSeats => prevSeats.filter(seat => seat._id !== id));
    return { success: true, message: json.message };
  } catch (error) {
    console.error('Error deleting seat:', error);
    return { success: false, message: error.message };
  }
}


// Assign seats
const  updateSeatStatus = async (seatId, slotName, bookedBy, seatValidTill = undefined) => {
  const slotPayload = { bookedBy };
  if (seatValidTill !== undefined && seatValidTill !== null && seatValidTill !== '') {
    slotPayload.seatValidTill = seatValidTill;
  }
  const seatStatusUpdate = {
      seatStatus: {
          [slotName]: slotPayload
      }
  };

  try {
      const response = await fetch(`${host}/seats/updateseatsdelete/${seatId}`, {
          method: 'PATCH',
          headers: {
              'Content-Type': 'application/json',
              "auth-token": localStorage.getItem('token'), // Authorization token
          },
          body: JSON.stringify(seatStatusUpdate),
      });

      if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
      }

    return response; // Return the fetch response for further processing

  } catch (error) {
      console.error('Failed to update seat status:', error);
      throw error; // Rethrow to handle in the calling function
  }
};


// Removes assgined seat
const removeAssignedSlot = async (seatId, slotName) => {
  const seatStatusUpdate = {
    seatStatus: {
      [slotName]: { bookedBy: "" } // Set bookedBy to an empty string to indicate removal
    }
  };

  try {
    const response = await fetch(`${host}/seats/emptyseat/${seatId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        "auth-token": localStorage.getItem('token'), // Authorization token
      },
      body: JSON.stringify(seatStatusUpdate),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    // Assuming the backend returns the updated seat object, refresh seat status in state
    const updatedSeat = await response.json();
    setSeats(prevSeats => prevSeats.map(seat => seat._id === seatId ? updatedSeat.seat : seat));
    return { ok: true, data: updatedSeat };
  } catch (error) {
    console.error('Failed to remove assigned slot:', error);
    // Optionally handle the error, e.g., by setting an error state or displaying a message
    return { ok: false, error: error.message };
  }
};

// Add a new seat
const addSeat = async (seatNumber, seatLocation) => {
  try {
    const response = await fetch(`${host}/seats/addaseat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': localStorage.getItem('token'),
      },
      body: JSON.stringify({ seatNumber, seatLocation, seatStatus: {} }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || 'Failed to add seat');
    }
    // Append the new seat to state
    setSeats(prevSeats => [...prevSeats, json]);
    return { success: true };
  } catch (error) {
    console.error('Error adding seat:', error);
    return { success: false, message: error.message };
  }
};

// Add bulk seats
const addBulkSeats = async (startingValue, numberOfSeats, seatLocation) => {
  try {
    const response = await fetch(`${host}/seats/addbulkseats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': localStorage.getItem('token'),
      },
      body: JSON.stringify({ startingValue, numberOfSeats, seatLocation }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || 'Failed to add bulk seats');
    }
    // Append the new seats to state
    setSeats(prevSeats => [...prevSeats, ...json.seats]);
    return { success: true, message: json.message };
  } catch (error) {
    console.error('Error adding bulk seats:', error);
    return { success: false, message: error.message };
  }
};

// Update seat metadata (seatNumber and seatLocation)
const updateSeat = async (id, seatNumber, seatLocation) => {
  try {
    const response = await fetch(`${host}/seats/updateseats/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': localStorage.getItem('token'),
      },
      body: JSON.stringify({ seatNumber, seatLocation }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || 'Failed to update seat');
    }
    // Update the seat in state
    setSeats(prevSeats => prevSeats.map(seat => seat._id === id ? json.seat || json : seat));
    return { success: true, seat: json.seat || json };
  } catch (error) {
    console.error('Error updating seat:', error);
    return { success: false, message: error.message };
  }
};

// New function to edit seat metadata
const editSeat = async (id, seatNumber, seatLocation) => {
  try {
    const response = await fetch(`${host}/seats/edit-seat/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'auth-token': localStorage.getItem('token'),
      },
      body: JSON.stringify({ seatNumber, seatLocation }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || 'Failed to edit seat');
    }
    setSeats(prevSeats => prevSeats.map(seat => seat._id === id ? json.seat : seat));
    return { success: true, seat: json.seat };
  } catch (error) {
    console.error('Error editing seat:', error);
    return { success: false, message: error.message };
  }
};

  return (
    <SeatContext.Provider value={{seats, setSeats, getAllSeats, addSeat, addBulkSeats, updateSeat, updateSeatStatus, removeAssignedSlot, editSeat, deleteSeat}}>
        {props.children}
    </SeatContext.Provider>
  )
}

export default SeatState



